/** Ephemeral ruler wire data. Coordinates and occupied sizes are always feet. */
export interface Ft3dRulerPoint {
    x: number; y: number; z: number;
    entity_id?: string;
    entity_kind?: 'creature' | 'inventory';
}
export interface Ft3dRulerFrame {
    type: 'ft3d_ruler' | 'ft3d_ruler_clear';
    schema: 'ft.ft3d.ruler.v1'; space: 'ft3d';
    encounter_id: string; campaign_id: string; actor_id: string; operation_id: string;
    ruler_id: string; from_client_id: string; sequence: number;
    points: Ft3dRulerPoint[]; finished: boolean;
    reach_ft: number; origin_size?: number | null; final_size?: number | null;
    color: string; from_user_id: string;
    from_kind: 'player_character' | 'encounter_creature' | 'gm' | 'user';
    from_entity_id: string | null; from_display_name: string; from_thumbnail_url: string | null;
    ts: number;
}
const uuid = (value: unknown): boolean => typeof value === 'string'
    && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value);
const bounded = (value: unknown): boolean => typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 100000;
export function isFt3dRulerFrame(value: unknown): value is Ft3dRulerFrame {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const row = value as Record<string, unknown>;
    if (typeof row.type !== 'string' || !['ft3d_ruler', 'ft3d_ruler_clear'].includes(row.type) || row.schema !== 'ft.ft3d.ruler.v1' || row.space !== 'ft3d'
        || !['encounter_id', 'campaign_id', 'ruler_id', 'from_client_id', 'operation_id', 'from_user_id'].every(key => uuid(row[key]))
        || !(row.actor_id === '' || uuid(row.actor_id)) || !Number.isSafeInteger(row.sequence) || Number(row.sequence) < 0
        || typeof row.finished !== 'boolean' || !bounded(row.reach_ft) || Number(row.reach_ft) < 0
        || !['origin_size', 'final_size'].every(key => row[key] == null || bounded(row[key]) && Number(row[key]) >= 0)
        || typeof row.color !== 'string' || !/^#[0-9a-f]{6}$/i.test(row.color)
        || typeof row.from_kind !== 'string' || !['player_character', 'encounter_creature', 'gm', 'user'].includes(row.from_kind)
        || !(row.from_entity_id === null || uuid(row.from_entity_id)) || typeof row.from_display_name !== 'string'
        || row.from_display_name.length > 512 || !(row.from_thumbnail_url === null || typeof row.from_thumbnail_url === 'string')
        || !Array.isArray(row.points) || row.points.length > 32 || typeof row.ts !== 'number' || !Number.isFinite(row.ts)) return false;
    if (row.type === 'ft3d_ruler_clear' && row.points.length !== 0) return false;
    return row.points.every((point: unknown) => {
        if (!point || typeof point !== 'object' || Array.isArray(point)) return false;
        const p = point as Record<string, unknown>;
        return ['x', 'y', 'z'].every(key => bounded(p[key]))
            && (p.entity_id === undefined && p.entity_kind === undefined
                || uuid(p.entity_id) && typeof p.entity_kind === 'string' && ['creature', 'inventory'].includes(p.entity_kind));
    });
}
